import type { Editor } from '@tiptap/react';

import { ChevronDownIcon } from 'lucide-react';
import { useState, useCallback, useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

import type { DropdownMenuWrapperProps, DropdownOption } from '.';

export function getFilteredOptions<T extends string | number>(options: DropdownOption<T>[], availableTypes: DropdownOption<T>['type'][]): DropdownOption<T>[] {
  return options.filter((option) => availableTypes.includes(option.type));
}

export function useActiveIcon<T extends string | number>(
  editor: Editor | null,
  filteredItems: DropdownOption<T>[],
  fallbackIcon: React.ReactNode,
  isActiveFn?: (editor: Editor | null, type: DropdownOption<T>['type']) => boolean
): () => React.ReactNode {
  return useCallback(() => {
    const activeOption = filteredItems.find((option) => isActiveFn?.(editor, option.type));
    return activeOption ? <activeOption.icon /> : fallbackIcon;
  }, [editor, filteredItems, isActiveFn, fallbackIcon]);
}

export function useDropdownMenuState<T extends string | number>(
  editor: Editor | null,
  availableTypes: DropdownOption<T>['type'][],
  options: DropdownOption<T>[],
  isActiveFn?: (editor: Editor | null, type: DropdownOption<T>['type']) => boolean,
  fallbackIcon?: React.ReactNode
) {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const filteredItems: DropdownOption<T>[] = useMemo(() => getFilteredOptions(options, availableTypes), [availableTypes]);

  const activeIcon = useActiveIcon(editor, filteredItems, fallbackIcon, isActiveFn);

  const handleOpenChange = useCallback((open: boolean, callback?: (isOpen: boolean) => void) => {
    setIsOpen(open);
    callback?.(open);
  }, []);

  return {
    activeIcon,
    isOpen,
    setIsOpen,
    filteredItems,
    handleOpenChange,
  };
}

export function DropdownMenuWrapper(dropdownMenuWrapperProps: DropdownMenuWrapperProps) {
  const { isOpen, onOpenChange, ariaLabel, activeIcon, isActive, isDisabled, buttonProps, children } = dropdownMenuWrapperProps;

  return (
    <DropdownMenu open={isOpen} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button type="button" data-style="ghost" data-active-state={isActive ? 'on' : 'off'} role="button" aria-label={ariaLabel} disabled={isDisabled} {...buttonProps}>
          {activeIcon}
          <ChevronDownIcon className={'opacity-50'} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-full max-w-xs">
        <DropdownMenuGroup>{children}</DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
